import './HorizontalBarChart.scss';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Coordinate,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
  YAxisTickContentProps
} from 'recharts';
import { Neutral700 } from '@src/utils/colors';
import { measureTextWidth } from '@src/utils/canvasTextUtils';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { Pagination } from '@Components/StickyTable/Pagination';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH } from '../ChartConstants';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';

const Y_AXIS_FONT_SIZE = 12;
const Y_AXIS_FONT = `${Y_AXIS_FONT_SIZE}px Geist, sans-serif`;
const DEFAULT_PAGE_LIMIT = 10;
// Room to the right of the longest bar for its end label ("1,637 (73%)").
const BAR_LABEL_MARGIN = 80;
const BAR_LABEL_GAP = 6;
// Category labels can run 80+ characters (e.g. full campaign/path names) — reserving enough
// width to show them in full would leave barely any room for the bars themselves. Both the
// truncation point and the axis width are driven by real canvas text measurement
// (measureTextWidth) rather than a character-count estimate: a fixed per-character ratio can't
// account for mixed content (numbers, pipes, all-caps runs all render at different widths), which
// previously caused the axis to be sized either too narrow (label wraps to a second line) or too
// generous (wasted blank space before the text). Measuring the actual string is exact either way,
// and it's synchronous — no post-render DOM measurement/ref timing to get wrong.
const Y_AXIS_MAX_WIDTH = 320;
const Y_AXIS_LINE_HEIGHT_EM = 1.2;

const truncateLabelToWidth = (label: string, maxWidth: number): string => {
  if (measureTextWidth(label, Y_AXIS_FONT) <= maxWidth) return label;
  let end = label.length;
  while (end > 1 && measureTextWidth(`${label.slice(0, end)}…`, Y_AXIS_FONT) > maxWidth) {
    end--;
  }
  return `${label.slice(0, end)}…`;
};

// Fills line 1 with as many whole words as fit, then truncates whatever's left onto line 2 (with
// an ellipsis if it still doesn't fit). This is our own measurement-driven wrap — see the note on
// YAxisTick below for why we can't let recharts do this itself.
const wrapLabelToTwoLines = (label: string, maxWidth: number): string[] => {
  if (measureTextWidth(label, Y_AXIS_FONT) <= maxWidth) return [label];

  const words = label.split(' ');
  let line1 = '';
  let wordIndex = 0;

  while (wordIndex < words.length) {
    const word = words[wordIndex];
    const candidate = line1 ? `${line1} ${word}` : word;
    if (measureTextWidth(candidate, Y_AXIS_FONT) <= maxWidth) {
      line1 = candidate;
      wordIndex++;
    } else if (line1) {
      break; // line 1 is as full as it can get — remaining words spill onto line 2
    } else {
      // Even the first word alone doesn't fit — hard-break it so we still make progress.
      line1 = truncateLabelToWidth(word, maxWidth).replace('…', '');
      wordIndex++;
      break;
    }
  }

  const remainder = words.slice(wordIndex).join(' ');
  return remainder ? [line1, truncateLabelToWidth(remainder, maxWidth)] : [line1];
};

// Recharts' default category tick renders through its <Text> component, which — whenever the
// YAxis has a numeric `width` — re-measures the string itself (via DOM getStringSize) and
// greedily word-wraps to fit, regardless of what tickFormatter already produced. That measurement
// doesn't agree with our canvas-based one, so it wraps at its own word boundary rather than ours.
// A custom `tick` element bypasses <Text> entirely — recharts calls it directly with the raw
// payload value, so this is the only place any measuring/truncation happens.
// Recharts clones this element with the real tick props (x/y/payload/etc.) at render time via
// cloneElement — the defaults below only need to satisfy the type for the prop-less <YAxisTick />
// JSX below and are never actually rendered.
const YAxisTick = ({
  x = 0,
  y = 0,
  payload = { value: '', coordinate: 0, index: 0 },
  textAnchor = 'end',
  fill = 'currentColor'
}: Partial<YAxisTickContentProps>) => {
  const label = String(payload.value);
  const lines = wrapLabelToTwoLines(label, Y_AXIS_MAX_WIDTH);
  return (
    <text
      x={x}
      y={y}
      dominantBaseline='central'
      textAnchor={textAnchor}
      fill={fill}
      fontSize={Y_AXIS_FONT_SIZE}
    >
      <title>{label}</title>
      {lines.map((line, i) => (
        <tspan
          key={i}
          x={x}
          dy={
            i === 0
              ? `${(-(lines.length - 1) * Y_AXIS_LINE_HEIGHT_EM) / 2}em`
              : `${Y_AXIS_LINE_HEIGHT_EM}em`
          }
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

// Text at the end of a bar. Drawn directly instead of through recharts' own label text, which
// wraps a label onto a second line when it is wider than the room it thinks it has.
const BarEndLabel = ({
  x,
  y,
  width,
  height,
  index,
  labels
}: {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  index?: number;
  labels: string[];
}) => {
  const label = index === undefined ? undefined : labels[index];
  if (label === undefined) return null;
  return (
    <text
      x={Number(x) + Number(width) + BAR_LABEL_GAP}
      y={Number(y) + Number(height) / 2}
      dominantBaseline='central'
      fontSize={Y_AXIS_FONT_SIZE}
      fill={Neutral700}
    >
      {label}
    </text>
  );
};

interface HorizontalBarChartProps<T> {
  data: T[];
  barKey: keyof T & string;
  barFillColor: string;
  yAxisKey: keyof T & string;
  yAxisLabel: string;
  height?: number;
  // Formats both the X axis ticks and the tooltip's bar value. Defaults to `toPrettyNumber` (raw
  // counts) — pass a custom formatter (e.g. a percent formatter) when `barKey` isn't a plain count.
  valueFormatter?: (value: number) => string;
  // Cross-filter support: clicking a bar reports its row, and the row matching
  // `selectedRowKey` (compared via `getRowKey`) is highlighted with `selectedFillColor`.
  // All four are optional and default to today's behavior (a single flat-colored, unclickable
  // bar) when omitted, so existing callers are unaffected.
  getRowKey?: (row: T) => string;
  selectedRowKey?: string;
  selectedFillColor?: string;
  onBarClick?: (row: T) => void;
  // Per-bar color (overrides `barFillColor`/the selected highlight) and a text label drawn at the
  // end of each bar, e.g. "360 (16%)". Both are optional and off by default.
  getBarColor?: (row: T) => string;
  getBarLabel?: (row: T) => string;
  // Bars per page; more rows than this show pagination. Defaults to 10.
  pageLimit?: number;
  // Makes `height` the plot's exact height instead of a minimum. Use it when the chart's card has
  // no height of its own: the plot is measured by AutoSizer, so a min-height plot with nothing
  // fixing its size grows every time the chart re-renders.
  fixedHeight?: boolean;
}

// Rough max width to keep the tooltip on-screen near the right edge — ChartTooltip sizes to its
// content, so this is a conservative estimate rather than an exact measurement.
const TOOLTIP_ESTIMATED_WIDTH = 260;
const TOOLTIP_VIEWPORT_MARGIN = 12;

interface HorizontalBarChartTooltipProps<T> {
  active?: boolean;
  payload?: { payload: T }[];
  coordinate?: Coordinate;
  plotEl: HTMLDivElement | null;
  barKey: keyof T & string;
  barFillColor: string;
  yAxisKey: keyof T & string;
  yAxisLabel: string;
  valueFormatter: (value: number) => string;
}

// Recharts renders its default Tooltip DOM node as a child of `.recharts-wrapper`, which sits
// inside ChartContainer's `overflow: hidden` card — near a card edge, the tooltip gets visually
// clipped instead of escaping the card the way it should (see reference_chartcontainer memory /
// SnapshotChartTooltip for the established fix). Portaling to `document.body` with a
// viewport-relative fixed position — converted from the plot's own bounding rect plus recharts'
// SVG-local `coordinate` — sidesteps the clip entirely. `plotEl` is state (not a ref) so reading
// its layout here doesn't trip the "no ref access during render" rule.
const HorizontalBarChartTooltip = <T,>({
  active,
  payload,
  coordinate,
  plotEl,
  barKey,
  barFillColor,
  yAxisKey,
  yAxisLabel,
  valueFormatter
}: HorizontalBarChartTooltipProps<T>) => {
  const plotRect = plotEl?.getBoundingClientRect();
  if (!active || !payload || payload.length === 0 || !coordinate || !plotRect) return null;
  const row = payload[0].payload;

  const left = Math.min(
    plotRect.left + coordinate.x + TOOLTIP_VIEWPORT_MARGIN,
    window.innerWidth - TOOLTIP_ESTIMATED_WIDTH
  );
  const top = Math.max(
    plotRect.top + coordinate.y - TOOLTIP_VIEWPORT_MARGIN,
    TOOLTIP_VIEWPORT_MARGIN
  );

  return createPortal(
    <div style={{ position: 'fixed', left, top, zIndex: 1000, pointerEvents: 'none' }}>
      <ChartTooltip
        headerLabel={String(row[yAxisKey])}
        items={[
          { label: yAxisLabel, value: valueFormatter(Number(row[barKey])), color: barFillColor }
        ]}
      />
    </div>,
    document.body
  );
};

// Exact width for the longest (already wrapped) line on this page, plus a little padding —
// never wider than it needs to be, never too narrow to fit what's actually rendered.
const yAxisWidth = <T,>(rows: T[], yAxisKey: keyof T & string): number =>
  rows.reduce((max, row) => {
    const lines = wrapLabelToTwoLines(String(row[yAxisKey]), Y_AXIS_MAX_WIDTH);
    const longestLine = lines.reduce(
      (m, line) => Math.max(m, measureTextWidth(line, Y_AXIS_FONT)),
      0
    );
    return Math.max(max, longestLine);
  }, 0) +
  Y_AXIS_FONT_SIZE * 1.5;

export const HorizontalBarChart = <T,>({
  data,
  barKey,
  barFillColor,
  yAxisKey,
  yAxisLabel,
  height = 400,
  valueFormatter = toPrettyNumber,
  getRowKey,
  selectedRowKey,
  selectedFillColor = barFillColor,
  onBarClick,
  getBarColor,
  getBarLabel,
  pageLimit = DEFAULT_PAGE_LIMIT,
  fixedHeight = false
}: HorizontalBarChartProps<T>) => {
  const [offset, setOffset] = useState(0);
  // Clamp rather than reset via effect: this component isn't remounted when the caller swaps in a
  // different `data` array (e.g. switching periods/filters upstream), so a stale offset from a
  // larger previous dataset would otherwise slice past the end of a smaller one — silently
  // rendering far fewer bars than `data` actually has, with no visible pagination control (since
  // `data.length <= pageLimit` hides it) to reveal why.
  const lastPageOffset = Math.max(0, Math.ceil(data.length / pageLimit) - 1) * pageLimit;
  const clampedOffset = Math.min(offset, lastPageOffset);
  const pageData = data.slice(clampedOffset, clampedOffset + pageLimit);
  const [plotEl, setPlotEl] = useState<HTMLDivElement | null>(null);

  // A per-row color wins; otherwise the selected row is highlighted and the rest use the base fill.
  const getCellFill = (row: T): string => {
    if (getBarColor) return getBarColor(row);
    return getRowKey?.(row) === selectedRowKey ? selectedFillColor : barFillColor;
  };

  return (
    <div className='horizontal-bar-chart'>
      <div
        className={
          fixedHeight
            ? 'horizontal-bar-chart-plot horizontal-bar-chart-plot-fixed'
            : 'horizontal-bar-chart-plot'
        }
        ref={setPlotEl}
        style={fixedHeight ? { height } : { minHeight: height }}
      >
        <AutoSizer
          renderProp={({ width = 0, height: sizerHeight = 0 }) => (
            <BarChart
              data={pageData}
              layout='vertical'
              width={width}
              height={sizerHeight}
              margin={getBarLabel ? { right: BAR_LABEL_MARGIN } : undefined}
            >
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                horizontal={false}
              />
              <XAxis
                type='number'
                axisLine={false}
                tickLine={false}
                tickFormatter={valueFormatter}
              />
              <YAxis
                type='category'
                dataKey={(row: T) => row[yAxisKey]}
                axisLine={false}
                tickLine={false}
                width={yAxisWidth(pageData, yAxisKey)}
                tick={<YAxisTick />}
                interval={0}
              />
              <Tooltip
                isAnimationActive={false}
                content={
                  <HorizontalBarChartTooltip<T>
                    plotEl={plotEl}
                    barKey={barKey}
                    barFillColor={barFillColor}
                    yAxisKey={yAxisKey}
                    yAxisLabel={yAxisLabel}
                    valueFormatter={valueFormatter}
                  />
                }
              />
              <Bar
                dataKey={(row: T) => row[barKey]}
                name={yAxisLabel}
                fill={barFillColor}
                isAnimationActive={false}
                onClick={
                  onBarClick
                    ? (barData: { payload?: T }) => {
                        if (barData.payload) {
                          onBarClick(barData.payload);
                        }
                      }
                    : undefined
                }
                style={onBarClick ? { cursor: 'pointer' } : undefined}
              >
                {(getRowKey || getBarColor) &&
                  pageData.map((row, index) => (
                    <Cell key={getRowKey ? getRowKey(row) : index} fill={getCellFill(row)} />
                  ))}
                {getBarLabel && (
                  <LabelList content={<BarEndLabel labels={pageData.map(getBarLabel)} />} />
                )}
              </Bar>
            </BarChart>
          )}
        />
      </div>
      {data.length > pageLimit && (
        <Pagination
          limit={pageLimit}
          offset={clampedOffset}
          totalData={data.length}
          updateOffset={setOffset}
        />
      )}
    </div>
  );
};
